/**
 * Lightweight value formatters used across feature pages.
 *
 * Keeping these in `@/lib/format` (instead of inline in each feature) means
 * the legacy `app.js` helpers (`formatCurrency`, `formatDate`, `shortId`)
 * can be replaced 1:1 by the migrated React pages, with consistent output
 * across the admin panel.
 *
 * All helpers tolerate `null` / `undefined` by returning a safe placeholder
 * so callers can pass API responses (whose fields are mostly optional in
 * the generated OpenAPI types) without a `??` dance at every call site.
 */

const currencyFormatter = new Intl.NumberFormat('pt-BR', {
  style: 'currency',
  currency: 'BRL',
});

/** Formats a number as BRL currency. `null`/`undefined` falls back to R$ 0,00. */
export const formatCurrency = (val: number | null | undefined): string =>
  currencyFormatter.format(val ?? 0);

/** Formats an ISO date-time string using PT-BR locale. Empty input returns `-`. */
export const formatDate = (val: string | null | undefined): string =>
  val ? new Date(val).toLocaleString('pt-BR') : '-';

/** Returns the first 8 characters of a UUID (or `-` for empty input). */
export const shortId = (uuid: string | null | undefined): string =>
  uuid ? uuid.substring(0, 8) : '-';

/**
 * Código exibido do pedido (RF-COD-05): o `displayCode` de loja+dia quando
 * existe, senão o identificador antigo — pedido anterior ao backfill, e pedido
 * ainda em `AWAITING_PAYMENT`, chegam com `null` e não podem quebrar a tela
 * (EC-COD-07). Por isso o fallback fica AQUI e não em cada `.tsx`: duplicar o
 * `??` é duplicar o lugar de esquecer o `displayCode`.
 *
 * O FORMATO também é decidido aqui, e é SEM "#" — é como o KDS escreve
 * (`kds/components/orderCode.ts`, mesmo invariante). Era exatamente essa
 * divergência que fazia o MESMO pedido ser `999003` no quadro da cozinha e
 * `#7dfe78bd` em Pedidos, no mesmo app. Nenhum JSX prefixa nada: o que sai
 * daqui já é o código final.
 */
export const orderDisplayCode = (
  displayCode: string | null | undefined,
  orderId: string | null | undefined,
): string => displayCode ?? shortId(orderId).toUpperCase();
